import React, {useCallback, useEffect} from 'react';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { REmployees, Role } from '../../../../features/roles/types';
import BaseForm from '../../../../components/shared/form-controls/base-form';
import { NAVIGATION_ROUTES } from '../../../../routes/NavigationRoute';
import { updateRole } from '../../../../features/roles';
import { getProfile } from '../../../../features/profile';
import { getPermissions } from '../../../../features/permissions';
import { getAllEmployees } from '../../../../features/employees/short';
import { filterRoles } from '../../../../features/roles/filter';
import { FormInput } from '../../../../components/shared/form-controls/form-input';
import { FormCheckbox } from '../../../../components/shared/form-controls/form-checkbox';
import FormAutocomplete from '../../../../components/shared/form-controls/form-autocomplete';
import { AutoCompleteItemValue } from '../../../../components/shared/form-controls/form-autocomplete/types';
import FormAccordion from '../../../../components/shared/form-controls/form-accordion';
import { AccordionDetailImpl, AccordionGroup, AccordionGroupImpl } from '../../../../components/shared/form-controls/form-accordion/types';
import { useIntl } from 'react-intl';
import { Lx } from '../../../../i18n/consts';
import { TextField } from '../text-field/text-field';
import { canManageRoleEmployees, canManageRolePermissions, canManageRoles } from '../../../../features/profile/entityPermissions';

interface EditForm {
    name: string;
    isActive: boolean;
    useForProjectsNotifications: boolean;
    useAsVacationPrimaryApprover: boolean;
    permissions?: number[];
    employees?: REmployees[];
}

export default function RoleEditForm() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const intl = useIntl();
    const profile = useAppSelector(store => store.profile);
    const rolesState = useAppSelector(store => store.roles);
    const permissions = useAppSelector(store => store.permissions);
    const employees = useAppSelector(store => store.rEmployees);
    const [isLoadingEmployees, setIsLoadingEmployees] = React.useState(false);

    const state = location.state as {role: Role} | undefined;
    const canEditRole = canManageRoles(profile);
    const canEditRoleEmployees = typeof state?.role?.canEditEmployees === 'boolean'
        ? state.role.canEditEmployees
        : rolesState.canShowAndEditEmployees || canManageRoleEmployees(profile);
    const canEditRolePermissions = typeof state?.role?.canEditPermissions === 'boolean'
        ? state.role.canEditPermissions
        : rolesState.canShowAndEditPermissions || canManageRolePermissions(profile);
    const initialValues: EditForm = {
        name: state?.role?.name ?? '',
        isActive: state?.role?.isActive ?? false,
        useForProjectsNotifications: state?.role?.useForProjectsNotifications ?? false,
        useAsVacationPrimaryApprover: state?.role?.useAsVacationPrimaryApprover ?? false,
        permissions: state?.role?.permissions,
        employees: state?.role?.employees ?? [],
    };

    useEffect(() => {
        if (!state || !canEditRole) {
            navigate(NAVIGATION_ROUTES.ROLES);
            return;
        }

        if (canEditRolePermissions && permissions.length === 0) {
            dispatch(getPermissions());
        }

    }, [canEditRole, canEditRolePermissions, dispatch, navigate, permissions.length, state]);


    const save = async (values: EditForm) => {
        const updated: Role = {
            id: state?.role?.id ?? 0,
            canEditEmployees: canEditRoleEmployees,
            canEditPermissions: canEditRolePermissions,
            employees: values.employees ?? [],
            isActive: values.isActive,
            useForProjectsNotifications: values.useForProjectsNotifications,
            useAsVacationPrimaryApprover: values.useAsVacationPrimaryApprover,
            name: values.name,
            permissions: values.permissions ?? [],
        }

        await dispatch(updateRole(updated)).unwrap();
        await dispatch(getProfile()).unwrap();
    }

    const ensureEmployeesLoaded = useCallback(() => {
        if (!canEditRoleEmployees || employees.length > 0 || isLoadingEmployees) {
            return;
        }

        setIsLoadingEmployees(true);
        dispatch(getAllEmployees())
            .finally(() => setIsLoadingEmployees(false));
    }, [canEditRoleEmployees, dispatch, employees.length, isLoadingEmployees]);


    if (!state || !canEditRole) {
        return null;
    }

    const initialHeader = state?.role?.name ?? '';
    const employeeOptions = employees.length ? employees : (state?.role?.employees ?? []);

    const validateName = async (name: string): Promise<boolean> => {
        name = name.toLowerCase();
        if (name === '') {
            return Promise.resolve(false);
        }
        return await dispatch(filterRoles(name)).unwrap().then((values) => {
            return values.length < 1 || values.some(value => value.name.toLowerCase() !== name) || name === initialValues.name.toLowerCase();
        });
    }
    const validationScheme: Yup.Schema<EditForm> = Yup.object().shape({
        name: Yup.string()
            .max(45, 'Must be 45 characters or less')
            .required(intl.formatMessage({ id: Lx.Form.NAME_REQUIRED_FORMAT }, { s: intl.formatMessage({ id: Lx.General.ROLE }) }))
            .test(
                'role-name-backend-validation',
                'Role name is taken',
                async (name: string) => { return await validateName(name);}
            ),
        isActive: Yup.boolean().required(),
        useForProjectsNotifications: Yup.boolean().required(),
        useAsVacationPrimaryApprover: Yup.boolean().required(),
        permissions: Yup.array().optional(),
        employees: Yup.array().of(Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required(),
            isDefault: Yup.boolean().required(),
        })).optional(),
    });

    const accordionGroups: AccordionGroup[] = permissions.map(group =>
        new AccordionGroupImpl(group.name,
            group.items.map(permission => {
                const result = new AccordionDetailImpl(permission.id, permission.name)
                result.selected = state?.role?.permissions.includes(permission.id);
                return result;
            })
        )) ?? []

    return <Formik
        initialValues={initialValues}
        validationSchema={validationScheme}
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={save}>
        <Form>
            <BaseForm contentGap={24} formType='edit'
                formHeader='edit_role'
                elementName={initialHeader} 
                savingTitle='Role' 
                onClose={() => navigate(NAVIGATION_ROUTES.ROLES)} 
                onAfterSave={() => navigate(NAVIGATION_ROUTES.ROLES)}>
                <div style={{display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', maxWidth: '500px'}}>
                    <TextField text={intl.formatMessage({ id: Lx.Role.MAIN_INFORMATION })} />
                    <FormInput name='name' label='Name' limited={true} required={true} placeholder='Name' style={{width: '100%', maxWidth: '500px', marginBottom: 0}} />
                </div>
                {canEditRoleEmployees && (
                    <div style={{width: '100%', maxWidth: '500px'}}>
                        <FormAutocomplete
                            name='employees'
                            label='Employees'
                            placeHolder='Start typing to add'
                            items={employeeOptions as AutoCompleteItemValue[]}
                            selectedItems={state?.role.employees}
                            hint='Leave empty to report on all'
                            onFocus={ensureEmployeesLoaded}
                        />
                    </div>
                )}
                <div style={{display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', maxWidth: '500px'}}>
                    <FormCheckbox name='isActive' label='Active' style={{marginBottom: 0}} />
                    <FormCheckbox name='useForProjectsNotifications' label='Use for projects notifications' style={{marginBottom: 0}} />
                    <FormCheckbox name='useAsVacationPrimaryApprover' label='Use as vacation primary approver' style={{marginBottom: 0}} />
                </div>
                {canEditRolePermissions && (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', maxWidth: '500px', marginTop: '8px'}}>
                        <TextField text={intl.formatMessage({ id: Lx.Role.PERMISSIONS })} />
                        <FormAccordion name='permissions' groups={accordionGroups} elementType={'Switch'} />
                    </div>
                )}
            </BaseForm>
        </Form>
    </Formik>
}
